import { useMultipleCameras } from "../hooks/useMultipleCameras";
import type { CameraState } from "../services/chimeVideo";

interface Props {
  cameraState: CameraState;
  isLocalVideoReady: boolean;
  retryProtectedVideo: () => Promise<void>;
  enableRawVideo: () => Promise<void>;
  closeVideo: () => Promise<void>;
  switchCamera: () => Promise<void>;
  connected: boolean;
  isReconnecting: boolean;
}

export default function VideoBackgroundControls({ cameraState, isLocalVideoReady, retryProtectedVideo, enableRawVideo, closeVideo, switchCamera, connected, isReconnecting }: Props) {
  const multipleCameras = useMultipleCameras(cameraState.status === "on");
  const loading = cameraState.status === "loading";
  const on = cameraState.status === "on" && isLocalVideoReady;
  const failed = cameraState.status === "error";
  const closeRequested = loading || (cameraState.status === "on" && (!isLocalVideoReady || cameraState.mode === "none" || !connected || isReconnecting));
  const action = loading ? "取消去背並關閉鏡頭"
    : closeRequested ? "關閉鏡頭"
      : on || failed ? "關閉背景，顯示實際環境" : "開啟去背鏡頭";
  const status = loading ? "已連線，攝影機準備中，尚未送出影像。"
    : on ? cameraState.mode === "replacement" ? "攝影機開啟：固定背景替換中" : "攝影機開啟：未去背，對方可看到實際環境"
      : "已連線，攝影機關閉；仍可使用語音及接收對方畫面。";
  const cycleCamera = () => {
    if (closeRequested) {
      void closeVideo();
    } else if (connected && !isReconnecting) {
      if (on || failed) void enableRawVideo();
      else void retryProtectedVideo();
    }
  };
  return (
    <>
      <button type="button" className={`control-btn camera-cycle ${on ? cameraState.mode === "replacement" ? "protected-camera" : "" : "disabled"}`}
        onClick={cycleCamera} disabled={!connected && !closeRequested}
        aria-label={action} title={`${status}；${action}`} aria-pressed={on}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d={on ? "M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" : "M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"} />
        </svg>
      </button>
      <span className="camera-status-sr" role="status">{status}</span>
      {multipleCameras && <button type="button" className="control-btn" disabled={!connected || !on || isReconnecting}
        onClick={switchCamera} aria-label="切換前後鏡頭" title="切換前後鏡頭">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5M6 7a7 7 0 0 1 12-1l2 3M18 17a7 7 0 0 1-12 1l-2-3" /></svg>
      </button>}
      {failed && <button type="button" className="control-btn" disabled={!connected || isReconnecting}
        onClick={retryProtectedVideo} aria-label="重試去背" title="重試去背">
        <i className="fas fa-redo" aria-hidden="true" />
      </button>}
      {cameraState.error && <span className="camera-control-error" role="alert">{cameraState.error}</span>}
    </>
  );
}
